import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError, finalize, shareReplay } from 'rxjs/operators';
import { TokenService } from './token.service';
import { HttpClient } from '@angular/common/http';
import { Nullable, RefreshTokenResponse } from '../types/internal-types';
import { LogHelper } from '../helpers/log.helper';
import { AccessToken, RefreshToken } from '../types/global-types';
import { InvalidTokenError } from 'jwt-decode';
import { ErrorHelper } from '../helpers/error.helper';
import { PkceHelper } from '../helpers/pkce.helper';
import { IdentityServiceInterface } from '../interfaces/identity-service.interface';
import { OptionsService } from './options.service';

@Injectable({ providedIn: 'root' })
export class IdentityService implements IdentityServiceInterface {

  constructor(
    private readonly tokenService: TokenService,
    private readonly httpClient: HttpClient,
    private readonly os: OptionsService
  ) {
    this.tokenService.accessToken.subscribe((accessToken) => this.accessToken.next(accessToken));
    this.tokenService.refreshToken.subscribe((refreshToken) => this.refreshToken.next(refreshToken));
  }
  readonly accessToken: BehaviorSubject<Nullable<AccessToken>> = new BehaviorSubject<Nullable<AccessToken>>(null);
  readonly refreshToken: BehaviorSubject<Nullable<RefreshToken>> = new BehaviorSubject<Nullable<RefreshToken>>(null);

  /**
   * a single in-flight refresh shared across all callers. Without this, every
   * request that 401s at the same time fires its own /refresh-token call — a
   * refresh storm that can rotate the refresh token out from under itself.
   */
  private refreshInProgress: Nullable<Observable<AccessToken>> = null;

  isAuthenticated():boolean {
    return !!this.tokenService.getAccessToken();
  }

  authenticate(): Observable<AccessToken> {

    /**
     * get access and refresh token
     */
    const accessToken: AccessToken = this.tokenService.getAccessToken();
    const refreshToken: RefreshToken = this.tokenService.getRefreshToken();

    /**
     * if it is available - return it; otherwise try refreshing the tokens by calling IDMS
     */
    if (accessToken) {
      return of(accessToken);
    }

    /**
     * if a refresh token is available - try getting new tokens
     */
    if (refreshToken) {
      return this.refreshTokens();
    }

    /**
     * throw error if no tokens are available
     */
    return throwError(new InvalidTokenError('No valid tokens found'));
  }

  refreshTokens(): Observable<AccessToken> {
    /**
     * a refresh is already running — hand every concurrent caller the same
     * shared stream instead of starting another /refresh-token call
     */
    if (this.refreshInProgress) {
		return this.refreshInProgress;
	}

    this.refreshInProgress = this.performRefresh().pipe(
      catchError((error: unknown) => {
        this.refreshInProgress = null;
        return throwError(error);
      }),
      finalize(() => {
        this.refreshInProgress = null;
      }),
      shareReplay(1)
    );

    return this.refreshInProgress;
  }

	/**
	 * exchange a one-time authorization code for a token pair.
	 *
	 * NOTE: `redirect_uri` MUST equal this app's IDMS `loginUrl` — i.e., the
	 * callback route we are currently on — so IDMS's bind-check on redemption
	 * passes. We derive it from the current location.
	 *
	 * PKCE (RFC 7636): send the `code_verifier` stashed by the launch redirect
	 * (PkceHelper); IDMS re-hashes it against the code's challenge. Cleared on
	 * read — it is single-use like the code.
	 */
	redeemCode(code: string): Observable<RefreshTokenResponse> {
		/**
		 * the verifier is single-use — read it and drop it immediately, regardless
		 * of the redeem outcome. The value is captured in the request body below, so
		 * a failed/replayed attempt never leaves a spent verifier in sessionStorage.
		 */
		const codeVerifier: Nullable<string> = PkceHelper.readVerifier();
		PkceHelper.clearVerifier();

		/**
		 * no verifier in sessionStorage → the launch-time writing was lost (e.g., the
		 * app → IDMS redirect committed before the writing flushed, or a different tab
		 * is redeeming). Fail LOUD instead of POSTing `code_verifier: ''`: an empty
		 * verifier can never satisfy IDMS's PKCE check, and sending it would burn the
		 * one-time code for good. Aborting here leaves the code unspent, so simply
		 * re-launching the app succeeds.
		 */
		if (!codeVerifier) {
			return throwError(new InvalidTokenError('Missing PKCE verifier; cannot redeem code'));
		}

		return this.httpClient.post<RefreshTokenResponse>(
			`${this.os.options.backendUrl}/api/v${this.os.options.apiVersion ?? 1}/redeem-code`,
			{
				code,
				client_id: this.os.options.application,
				redirect_uri: `${window.location.origin}${window.location.pathname}`,
				code_verifier: codeVerifier,
			}
		);
	}

	clearTokens():void {
		this.tokenService.clearTokens();
	}

  private performRefresh(): Observable<AccessToken> {
    const refreshToken: RefreshToken = this.tokenService.getRefreshToken();

    /**
     * throw error if the refresh token is not available
     */
    if (!refreshToken) {
      return throwError(new InvalidTokenError('No refresh token found'));
    }

    /**
     * make a request to Identity service to get new tokens
     */
    return this.httpClient
      .get<RefreshTokenResponse>(
        `${this.os.options.backendUrl}/api/v${this.os.options.apiVersion ?? 1}/refresh-token`,
        {
          headers: { Authorization: `Bearer ${refreshToken.token}` },
        }
      )
      .pipe(
        catchError((error: unknown) => {
          /**
           * log error if it happens
           */
          LogHelper.error(
            new InvalidTokenError(`Failed to obtain tokens. Reason: ${ErrorHelper.extractMessage(error).toLowerCase()}`)
          );

          return throwError(error);
        }),
        map((response) => {
          /**
           * set refresh token
           */
          this.tokenService.setRefreshToken(response.refresh_token);

          /**
           * set and return access token
           */
          return this.tokenService.setAccessToken(response.access_token);
        })
      );
  }

}
